import { useEffect, useState } from 'react';
import { PreviewOptions, ChatStats } from '../types/preview';
import { Settings, Eye, Palette, Layout, Image, Filter, Type, ChevronDown, Download, Loader2 } from 'lucide-react';

interface PreviewSidebarProps {
  options: PreviewOptions;
  setOptions: (options: PreviewOptions) => void;
  stats: ChatStats;
  agbCheckbox: boolean;
  setAgbCheckbox: (v: boolean) => void;
  widerrufsCheckbox: boolean;
  setWiderrufsCheckbox: (v: boolean) => void;
  onCheckout: () => void;
  isRendering: boolean;
  onFreePdf?: () => void;
  isGeneratingFreePdf?: boolean;
}

export function PreviewSidebar({
  options,
  setOptions,
  stats,
  agbCheckbox,
  setAgbCheckbox,
  widerrufsCheckbox,
  setWiderrufsCheckbox,
  onCheckout,
  isRendering,
  onFreePdf,
  isGeneratingFreePdf = false,
}: PreviewSidebarProps) {
  
  // Accordion: track which sections are open (multiple can be open)
  const [openSections, setOpenSections] = useState<string[]>(['header', 'display']);

  // Initialize selectedSenders with ALL participants when stats load
  useEffect(() => {
    if (stats.participants.length > 0 && options.selectedSenders.length === 0) {
      setOptions({ ...options, selectedSenders: [...stats.participants] });
    }
  }, [stats.participants]);

  const toggleSection = (id: string) => {
    setOpenSections(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const sections = [
    { id: 'header', label: 'Kopfzeile', icon: Type },
    { id: 'display', label: 'Anzeige', icon: Eye },
    { id: 'colors', label: 'Farben', icon: Palette },
    { id: 'layout', label: 'Layout', icon: Layout },
    { id: 'media', label: 'Medien', icon: Image },
    { id: 'filter', label: 'Filter', icon: Filter },
  ];

  const updateOption = <K extends keyof PreviewOptions>(
    key: K, 
    value: PreviewOptions[K]
  ) => {
    setOptions({ ...options, [key]: value });
  };

  const sectionContent: Record<string, React.ReactNode> = {
    header: (
      <>
        <label className="option-item">
          <input
            type="checkbox"
            checked={options.showHeader}
            onChange={(e) => updateOption('showHeader', e.target.checked)}
          />
          <span>Grünes Header-Banner anzeigen</span>
        </label>
        
        <div
          className="suboption-group"
          style={{ opacity: options.showHeader ? 1 : 0.4, pointerEvents: options.showHeader ? 'auto' : 'none' }}
        >
          <div className="option-row">
            <label className="option-label">Titel</label>
            <input
              type="text"
              value={options.exportTitle}
              onChange={(e) => updateOption('exportTitle', e.target.value)}
              className="option-text-input"
              placeholder="WhatsApp Chat Export"
            />
          </div>

          <label className="option-item">
            <input
              type="checkbox"
              checked={options.showHeaderParticipants}
              onChange={(e) => updateOption('showHeaderParticipants', e.target.checked)}
            />
            <span>Teilnehmeranzahl anzeigen</span>
          </label>
          
          <label className="option-item">
            <input
              type="checkbox"
              checked={options.showHeaderStats}
              onChange={(e) => updateOption('showHeaderStats', e.target.checked)}
            />
            <span>Zeitraum & Statistiken</span>
          </label>
        </div>
      </>
    ),

    display: (
      <>
        <label className="option-item">
          <input
            type="checkbox"
            checked={options.showSystemMessages}
            onChange={(e) => updateOption('showSystemMessages', e.target.checked)}
          />
          <span>System-Nachrichten</span>
        </label>
        
        <label className="option-item">
          <input
            type="checkbox"
            checked={options.showEditMarkers}
            onChange={(e) => updateOption('showEditMarkers', e.target.checked)}
          />
          <span>„Bearbeitet"-Hinweis</span>
        </label>
        
        <label className="option-item">
          <input
            type="checkbox"
            checked={options.showDateHeaders}
            onChange={(e) => updateOption('showDateHeaders', e.target.checked)}
          />
          <span>Datum-Trennlinien</span>
        </label>
        
        <label className="option-item">
          <input
            type="checkbox"
            checked={options.showTimestamps}
            onChange={(e) => updateOption('showTimestamps', e.target.checked)}
          />
          <span>Zeitstempel</span>
        </label>
        
        <label className="option-item">
          <input
            type="checkbox"
            checked={options.showSenderNames}
            onChange={(e) => updateOption('showSenderNames', e.target.checked)}
          />
          <span>Absender-Namen</span>
        </label>
      </>
    ),

    colors: (
      <>
        <label className="option-item" style={{ marginBottom: '12px' }}>
          <input
            type="checkbox"
            checked={options.showWhatsAppBackground}
            onChange={(e) => updateOption('showWhatsAppBackground', e.target.checked)}
          />
          <span>WhatsApp Doodle-Muster</span>
        </label>

        {[
          { key: 'exporteurColor' as const, label: 'Eigene Nachrichten', hint: stats.exporteur },
          { key: 'otherColor' as const, label: 'Empfangene Nachrichten', hint: '' },
          { key: 'systemColor' as const, label: 'System-Nachrichten', hint: '' },
          { key: 'backgroundColor' as const, label: 'Chat-Hintergrund', hint: '' },
        ].map(({ key, label, hint }) => (
          <div key={key} className="color-option">
            <div className="color-option-header">
              <label>{label}</label>
              {hint && <small className="color-hint">{hint}</small>}
            </div>
            <div className="color-picker-wrapper">
              <input
                type="color"
                value={(options as any)[key]}
                onInput={(e) => updateOption(key, (e.target as HTMLInputElement).value)}
                onChange={(e) => updateOption(key, (e.target as HTMLInputElement).value)}
              />
              <span className="color-value">{(options as any)[key]}</span>
            </div>
          </div>
        ))}
      </>
    ),

    layout: (
      <>
        <div className="option-row">
          <label className="option-label">Blasen-Style</label>
          <div className="segmented-control">
            {(['rounded', 'square'] as const).map(val => (
              <button
                key={val}
                className={`seg-btn ${options.bubbleStyle === val ? 'active' : ''}`}
                onClick={() => updateOption('bubbleStyle', val)}
              >
                {val === 'rounded' ? '◉ Rund' : '▣ Eckig'}
              </button>
            ))}
          </div>
        </div>

        <div className="option-row">
          <label className="option-label">Blasen-Breite</label>
          <div className="segmented-control">
            {([['compact', 'Schmal'], ['normal', 'Normal'], ['wide', 'Breit']] as const).map(([val, lbl]) => (
              <button
                key={val}
                className={`seg-btn ${options.bubbleSize === val ? 'active' : ''}`}
                onClick={() => updateOption('bubbleSize', val)}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="option-row">
          <label className="option-label">Schriftgröße</label>
          <div className="segmented-control">
            {([['small', 'Klein'], ['normal', 'Normal'], ['large', 'Groß']] as const).map(([val, lbl]) => (
              <button
                key={val}
                className={`seg-btn ${options.fontSize === val ? 'active' : ''}`}
                onClick={() => updateOption('fontSize', val)}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="option-row">
          <label className="option-label">Zeilenabstand</label>
          <div className="segmented-control">
            {([['compact', 'Eng'], ['normal', 'Normal'], ['wide', 'Weit']] as const).map(([val, lbl]) => (
              <button
                key={val}
                className={`seg-btn ${options.lineHeight === val ? 'active' : ''}`}
                onClick={() => updateOption('lineHeight', val)}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
      </>
    ),

    media: (
      <>
        <div className="option-row">
          <label className="option-label">Medien-Modus</label>
          <select
            value={options.mediaMode}
            onChange={(e) => updateOption('mediaMode', e.target.value as any)}
            className="option-select"
          >
            <option value="both">Standard (Thumbnail + Link)</option>
            <option value="thumbnail">Kompakt (Nur Thumbnail)</option>
            <option value="link">Speicher-Sparer (Nur Link)</option>
            <option value="hidden">Medien ausblenden</option>
          </select>
        </div>

        <div className="option-row" style={{ opacity: options.mediaMode === 'link' || options.mediaMode === 'hidden' ? 0.4 : 1, pointerEvents: options.mediaMode === 'link' || options.mediaMode === 'hidden' ? 'none' : 'auto' }}>
          <label className="option-label">Thumbnail-Größe</label>
          <div className="segmented-control">
            {([['small', 'Klein'], ['normal', 'Normal'], ['large', 'Groß']] as const).map(([val, lbl]) => (
              <button
                key={val}
                className={`seg-btn ${options.thumbnailSize === val ? 'active' : ''}`}
                onClick={() => updateOption('thumbnailSize', val)}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <label className="option-item" style={{ opacity: options.mediaMode === 'hidden' ? 0.4 : 1, pointerEvents: options.mediaMode === 'hidden' ? 'none' : 'auto' }}>
          <input
            type="checkbox"
            checked={options.showVideoOverlay}
            onChange={(e) => updateOption('showVideoOverlay', e.target.checked)}
          />
          <span>Play-Button auf Videos</span>
        </label>
      </>
    ),

    filter: (
      <>
        <div className="option-row">
          <label className="option-label">Text-Suche</label>
          <input
            type="text"
            placeholder="Nach Text suchen..."
            value={options.searchText}
            onChange={(e) => updateOption('searchText', e.target.value)}
            className="option-text-input"
          />
        </div>

        {stats.participants.length > 0 && (
          <div className="filter-group">
            <div className="filter-group-header">
              <span>Teilnehmer</span>
              <button
                className="filter-toggle-all"
                onClick={() => {
                  const allSelected = stats.participants.every(p => options.selectedSenders.includes(p));
                  updateOption('selectedSenders', allSelected ? [] : [...stats.participants]);
                }}
              >
                {stats.participants.every(p => options.selectedSenders.includes(p)) ? 'Alle abwählen' : 'Alle wählen'}
              </button>
            </div>
            {stats.participants.map(sender => (
              <label key={sender} className="option-item chip-item">
                <input
                  type="checkbox"
                  checked={options.selectedSenders.includes(sender)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      updateOption('selectedSenders', [...options.selectedSenders, sender]);
                    } else {
                      updateOption('selectedSenders', options.selectedSenders.filter(s => s !== sender));
                    }
                  }}
                />
                <span className="chip-label">{sender}</span>
              </label>
            ))}
          </div>
        )}

        <div className="filter-group">
          <div className="filter-group-header">
            <span>Medien-Typen</span>
          </div>
          {(['image', 'video', 'audio', 'document'] as const).map(type => (
            <label key={type} className="option-item chip-item">
              <input
                type="checkbox"
                checked={options.mediaFilter.includes(type)}
                onChange={(e) => {
                  if (e.target.checked) {
                    updateOption('mediaFilter', [...options.mediaFilter, type]);
                  } else {
                    updateOption('mediaFilter', options.mediaFilter.filter(t => t !== type));
                  }
                }}
              />
              <span className="chip-label">
                {type === 'image' && '📷 Bilder'}
                {type === 'video' && '🎥 Videos'}
                {type === 'audio' && '🎵 Audio'}
                {type === 'document' && '📄 Dokumente'}
              </span>
            </label>
          ))}
        </div>
      </>
    ),
  };

  return (
    <div className="preview-sidebar">
      <div className="sidebar-title">
        <Settings size={18} />
        <span>Optionen</span>
      </div>

      <div className="accordion">
        {sections.map(section => {
          const Icon = section.icon;
          const isOpen = openSections.includes(section.id);
          return (
            <div key={section.id} className={`accordion-item ${isOpen ? 'open' : ''}`}>
              <button
                className="accordion-header"
                onClick={() => toggleSection(section.id)}
                aria-expanded={isOpen}
              >
                <div className="accordion-header-left">
                  <Icon size={15} />
                  <span>{section.label}</span>
                </div>
                <div className={`accordion-chevron ${isOpen ? 'rotated' : ''}`}>
                  <ChevronDown size={15} />
                </div>
              </button>
              {isOpen && (
                <div className="accordion-content">
                  {sectionContent[section.id]}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Stats Footer */}
      <div className="sidebar-stats">
        <div className="stat-item">
          <span className="stat-label">Nachrichten</span>
          <span className="stat-value">{stats.totalMessages}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Teilnehmer</span>
          <span className="stat-value">{stats.participants.length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Medien</span>
          <span className="stat-value">{stats.mediaCount}</span>
        </div>
      </div>

      {/* Checkout Section */}
      <div className="sidebar-checkout">
        <p className="sidebar-checkout-title">Fertig? PDF erstellen</p>
        <label className="widerruf-checkbox">
          <input
            type="checkbox"
            checked={agbCheckbox}
            onChange={(e) => setAgbCheckbox(e.target.checked)}
          />
          <span>
            Ich akzeptiere die{' '}
            <a href="/agb.html" target="_blank" rel="noopener">AGB</a> und{' '}
            <a href="/datenschutz.html" target="_blank" rel="noopener">Datenschutzerklärung</a>.
          </span>
        </label>
        <label className="widerruf-checkbox">
          <input
            type="checkbox"
            checked={widerrufsCheckbox}
            onChange={(e) => setWiderrufsCheckbox(e.target.checked)}
          />
          <span>
            Ich stimme zu, dass sofort mit der Erstellung begonnen wird und verliere damit mein Widerrufsrecht.
          </span>
        </label>
        <button
          className="sidebar-checkout-btn"
          disabled={!agbCheckbox || !widerrufsCheckbox || isRendering}
          onClick={onCheckout}
        >
          {isRendering
            ? <><Loader2 size={16} className="spinning" /> PDF wird erstellt...</>
            : <><Download size={16} /> Vollständiges PDF — €4,99</>
          }
        </button>

        {onFreePdf && (
          <div className="sidebar-free">
            <div className="sidebar-free-divider"><span>oder</span></div>
            <button
              className="sidebar-free-btn"
              onClick={onFreePdf}
              disabled={isGeneratingFreePdf}
            >
              {isGeneratingFreePdf
                ? <><Loader2 size={14} className="spinning" /> Wird erstellt...</>
                : <><Download size={14} /> Kostenlos herunterladen</>
              }
            </button>
            <p className="sidebar-free-hint">50 Nachrichten · mit Wasserzeichen</p>
          </div>
        )}
      </div>
    </div>
  );
}
